import React, { useEffect } from "react";
import { X } from "lucide-react";
import { avatarSrc } from "@/lib/defaults";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Lot 729 — « Qui a réagi » (façon WhatsApp) : feuille listant chaque personne et son emoji sous la bulle touchée.
// Chat privé : les réacteurs sont moi ou l'autre participant. Ma ligne → « Touche pour retirer » (toggle existant).
export const reactorRows = (reactions, user, peer, t) => Object.entries(reactions || {}).map(([uid, emoji]) => {
  const mine = uid === user.id;
  const who = mine ? user : (peer && peer.id === uid ? peer : null);
  return { uid, emoji, mine, name: mine ? t("chat.you_cap", "Vous") : (who?.name || t("chat.member", "Membre")), who };
});

export const ReactionsSheet = ({ message, user, peer, t, isMobile, onClose, onRemoveMine }) => {
  const open = !!message;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const rows = reactorRows(message.reactions, user, peer, t);
  return (
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/50" data-testid="chat-reactions-sheet" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: isMobile ? "var(--sa-bottom)" : undefined }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="font-bold text-sm flex-1" style={{ color: "var(--yalola-text)" }} data-testid="chat-reactions-title">
            {t("chat.reactions_title", "Réactions")} <span className="font-semibold" style={{ color: "var(--yalola-text-muted)" }}>· {rows.length}</span>
          </div>
          <button onClick={onClose} className="h-8 w-8 -mr-2 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" data-testid="chat-reactions-close" aria-label={t("common.close", "Fermer")}><X size={18} /></button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1" data-testid="chat-reactions-list">
          {rows.length === 0 && (
            <div className="px-4 py-6 text-sm text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-reactions-empty">{t("chat.reactions_none", "Plus aucune réaction")}</div>
          )}
          {rows.map((r) => {
            const inner = (
              <>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={r.who ? avatarSrc(r.who) : undefined} />
                  <AvatarFallback>{(r.name || "?")[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate" style={{ color: "var(--yalola-text)" }} data-testid={`chat-reaction-name-${r.uid}`}>{r.name}</div>
                  {r.mine && <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.reaction_tap_remove", "Touche pour retirer")}</div>}
                </div>
                <span className="text-2xl leading-none" data-testid={`chat-reaction-emoji-${r.uid}`}>{r.emoji}</span>
              </>
            );
            return r.mine ? (
              <button key={r.uid} type="button" onClick={() => onRemoveMine(message.id, r.emoji)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--yalola-surface-alt)] transition-colors active:scale-[0.99]" data-testid={`chat-reaction-row-${r.uid}`}>{inner}</button>
            ) : (
              <div key={r.uid} className="w-full flex items-center gap-3 px-4 py-2.5" data-testid={`chat-reaction-row-${r.uid}`}>{inner}</div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
